import { action, computed, observable } from "mobx";

import { ChangePageButton } from "./MostPlayedTicketsStore";
import { LoaderStore } from "@state/stores/common";
import { BetSlipBetType } from "@gp/slip";
import {
	BetBuilderPick,
	TBetBuilderPick,
} from "@data-types/bet-booster/BetBuilderPicks";
import { ObservableMap } from "mobx";
import type { RootStore } from "@state/stores/types";
import { createMultiOfferTip } from "@utils/common/createMultiOfferTip";

export class BetBuilderPicksStore {
	@observable isStoreInitialized: boolean = false;
	@observable BBPicks = new ObservableMap<string, BetBuilderPick>();
	@observable sportCategories: TSportCategories[] = observable.array([]);
	@observable public selectedSportCategory: TSportCategories | null = null;
	@observable public filteredBBPicks = new ObservableMap<
		string,
		BetBuilderPick
	>();
	@observable isPopupDisplayed: boolean = false;
	@observable isFilteredList: boolean = false;

	@observable public blockMainLoader: boolean = false;
	@observable public isAddingOrRemovingPick: boolean = false;
	@observable BBPickNotAvailablePopup: boolean = false;
	@observable public selectedBetSlip: BetBuilderPick | null = null;
	public slipIndex: number;

	rootStore: RootStore;
	loader: LoaderStore;
	@observable betBuilderPresetIds: string[];
	@observable betBuilderPresets: Map<string, TBetBuilderPick>;

	@computed get isLoading() {
		return this.loader.isLoading;
	}

	@computed get isSelectedMultiOfferOnSlip() {
		return this.rootStore.betSlipStore.betSlipState
			.isSelectedMultiOfferOnSlip;
	}

	@computed get currentActiveList() {
		return this.isFilteredList ? this.filteredBBPicks : this.BBPicks;
	}

	constructor(rootStore: RootStore) {
		this.loader = new LoaderStore();
		this.rootStore = rootStore;
		this.checkIsSelectedMultiOfferOnSlip =
			this.checkIsSelectedMultiOfferOnSlip.bind(this);
		this.addOrRemoveBetBuilderPickOnSlip =
			this.addOrRemoveBetBuilderPickOnSlip.bind(this);
	}

	@action.bound
	setIsAddingOrRemovingPick(value: boolean) {
		this.isAddingOrRemovingPick = value;
	}

	@action.bound
	public onInitializeBetBuilderPicks(data: {
		betBuilderPresetIds: string[];
		betBuilderPresets: Map<string, TBetBuilderPick>;
	}) {
		this.betBuilderPresetIds = data.betBuilderPresetIds;
		this.betBuilderPresets = data.betBuilderPresets;
		this.mapBetBuilderPicks();
		this.isStoreInitialized = true;
	}

	@action.bound
	mapBetBuilderPicks() {
		const BBPicks = this?.betBuilderPresets;
		if (!BBPicks?.size) {
			console.error("no bbpicks");
			return;
		}

		const mappedBetSlips =
			this.createBetSlipWithMappedBettingOffers(BBPicks);
		if (this.selectedBetSlip) {
			if (
				this.betBuilderPresetIds.find(
					(id) => id == this.selectedBetSlip?.id
				)
			) {
				const slip = mappedBetSlips.get(this.selectedBetSlip?.id);
				if (slip) {
					this.selectedBetSlip = slip;
				}
			}
		}
		this.BBPicks = mappedBetSlips;

		this.createBBPickSportsCategories();

		[this.selectedSportCategory] = this.sportCategories;
		if (this.sportCategories.length > 0) {
			this.filterByCategory(this.sportCategories[0]?.sportAbrv);
		}
	}

	@action.bound
	updateBetBuilderPick(slip: TBetBuilderPick) {
		const bbSlip = this.BBPicks.get(slip.id);
		if (bbSlip) {
			bbSlip.indicator = slip.indicator;
		}
	}
	@action.bound
	activatePopup(id: string, index: number, isFilteredList: boolean) {
		this.isFilteredList = isFilteredList;
		this.isPopupDisplayed = true;
		this.slipIndex = index;
		const slip = this.currentActiveList.get(id);
		if (slip) {
			this.selectedBetSlip = slip;
		}
	}

	@action.bound
	deactivatePopup() {
		this.isPopupDisplayed = false;
		this.isFilteredList = false;
		this.selectedBetSlip = null;
	}

	@action.bound
	setBBPicksAvailabilityPopup(value: boolean) {
		this.BBPickNotAvailablePopup = value;
	}

	@action.bound
	changePage(num: ChangePageButton) {
		if (!this.blockMainLoader) this.blockMainLoader = true;
		const BBPicks = this.isFilteredList
			? this.filteredBBPicks
			: this.BBPicks;

		const BBPicksArray = Array.from(BBPicks);
		const totalSize = BBPicksArray.length;

		let currentIndex = BBPicksArray.findIndex(
			([key]) => key === this.selectedBetSlip?.id
		);

		switch (num) {
			case ChangePageButton.PREVIOUS:
				if (currentIndex > 0) {
					currentIndex -= 1;
				} else {
					currentIndex = totalSize - 1;
				}
				break;
			case ChangePageButton.NEXT:
				if (currentIndex < totalSize - 1) {
					currentIndex += 1;
				} else {
					currentIndex = 0;
				}
				break;
			default:
				currentIndex = num;
				break;
		}

		this.selectedBetSlip = BBPicksArray[currentIndex][1];
		this.slipIndex = currentIndex;
	}

	async addOrRemoveBetBuilderPickOnSlip(BBPick: BetBuilderPick) {
		this.setIsAddingOrRemovingPick(true);

		if (this.checkIsSelectedMultiOfferOnSlip(BBPick)) {
			//pick is in slip
			await this.rootStore.betSlipStore.forcefullyRemovePresetMultiOfferOffer(
				BBPick
			);
		} else {
			await this.addBetBuilderPickToSlip(BBPick);
		}

		const betSlipState = this.rootStore.betSlipStore.betSlipState;

		const isRegularEventPresent = betSlipState.isRegularEventSelected(
			BBPick.eventId
		);
		const isWegePresent = betSlipState.isEventWithMultipleOffersPresent();
		const isSystemSlip = betSlipState.betSlipType === "system";

		if (!(isRegularEventPresent || isWegePresent || isSystemSlip)) {
			this.rootStore.betBuilderStore.updateMultiOfferQuotas([
				{
					id: BBPick.eventId,
					liveStatus: BBPick.isLive ? 1 : 0,
				},
			]);
		}

		this.setIsAddingOrRemovingPick(false);
	}

	checkIsSelectedMultiOfferOnSlip(BBPick: BetBuilderPick) {
		const multiOfferIds = BBPick.multiOffers.map((mo) => mo.bettingOfferId);
		return this.isSelectedMultiOfferOnSlip(BBPick.eventId, multiOfferIds);
	}

	@action.bound
	async addBetBuilderPickToSlip(BBPick: BetBuilderPick) {
		try {
			const pickAppendModel = createMultiOfferTip({
				eventId: BBPick.eventId,
				multiOffers: BBPick.multiOffers,
				value: BBPick.bbMaxCoefficient,
			});

			//switch to normal if on one click bet
			this.rootStore.betSlipStore.betSlipState.onBetSlipBetTypeChange(
				BetSlipBetType.Normal
			);

			this.rootStore.betSlipStore.addRemoveOffer(pickAppendModel);
		} catch (error) {
			console.error(error);
			this.setBBPicksAvailabilityPopup(true);
		}
	}

	@action.bound
	createAppendModel(BBPick: BetBuilderPick) {
		return {
			eventId: BBPick.eventId,
			value: BBPick.bbMaxCoefficient,
			isBank: false,
			multiOffers: BBPick.multiOffers.map((mo) => ({
				multiOfferId: mo.bettingOfferId,
				value: mo.value,
			})),
		};
	}

	@action.bound
	private createBetSlipWithMappedBettingOffers(
		BBPicks: Map<string, TBetBuilderPick>
	) {
		const mappedBettingOffer = new ObservableMap<string, BetBuilderPick>();
		this.betBuilderPresetIds.forEach((bsId) => {
			const bbPick = BBPicks.get(bsId);
			if (bbPick) {
				mappedBettingOffer.set(bsId, new BetBuilderPick(bbPick));
			}
		});
		return mappedBettingOffer;
	}

	//quikc play page filter per sport
	@action.bound
	private createBBPickSportsCategories() {
		const sportCategoriesSet = new Map<string, TSportCategories>();
		this.BBPicks.forEach((BBPick) => {
			if (!sportCategoriesSet.has(BBPick.sportAbrv)) {
				sportCategoriesSet.set(BBPick.sportAbrv, {
					sportAbrv: BBPick.sportAbrv,
					sortOrder: BBPick.sportSortOrder,
					sportName: BBPick.sportName,
				});
			}
		});

		const sortedSportCategories = [...sportCategoriesSet.values()].sort(
			(a, b) => a.sortOrder - b.sortOrder
		);

		this.sportCategories = sortedSportCategories;
	}

	@action.bound
	public filterByCategory(sportAbrv?: string) {
		if (!sportAbrv) {
			this.filteredBBPicks = this.BBPicks;
			return;
		}
		this.filteredBBPicks.clear();
		let selectedSportCategory: TSportCategories | null = null;
		this.betBuilderPresetIds.forEach((bsId) => {
			const bbPick = this.BBPicks.get(bsId);
			if (!bbPick) {
				return;
			}

			if (bbPick.sportAbrv == sportAbrv) {
				this.filteredBBPicks.set(bbPick.id, bbPick);
				if (!selectedSportCategory) {
					selectedSportCategory = {
						sportAbrv: bbPick.sportAbrv,
						sortOrder: bbPick.sportSortOrder,
						sportName: bbPick.sportName,
					};
				}
			}
		});

		this.selectedSportCategory = selectedSportCategory;
	}

	@action.bound
	onDispose() {
		this.blockMainLoader = false;
		this.isStoreInitialized = false;
		this.BBPicks.clear();
		this.sportCategories = [];
		this.filteredBBPicks.clear();
		this.isFilteredList = false;
		this.isPopupDisplayed = false;
		this.isAddingOrRemovingPick = false;
		this.selectedBetSlip = null;
		this.betBuilderPresetIds = [];
		this.betBuilderPresets?.clear();
	}
}

type TSportCategories = {
	sportAbrv: string;
	sortOrder: number;
	sportName: string;
};
